import React from 'react';
import { CheckCircle, Loader2, AlertCircle, Clock, Radio } from 'lucide-react';
import { DocumentProgressStatusEnum } from '@/types/document-store.types';
import { getStatusDisplay } from '@/hooks/useRealTimeStatus';

interface StatusIndicatorProps {
  status: DocumentProgressStatusEnum | string;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showPulse?: boolean;
  className?: string;
}

export default function StatusIndicator({ 
  status, 
  showLabel = true, 
  size = 'md', 
  showPulse = false,
  className = '' 
}: StatusIndicatorProps) {
  const statusDisplay = getStatusDisplay(status);
  
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4', 
    lg: 'w-5 h-5',
  };

  const getIcon = () => {
    const iconSize = sizeClasses[size];
    
    switch (status) {
      case DocumentProgressStatusEnum.DONE:
        return <CheckCircle className={`${iconSize} text-green-500`} />;
      case DocumentProgressStatusEnum.IN_PROGRESS:
        return <Loader2 className={`${iconSize} text-blue-500 animate-spin`} />;
      case DocumentProgressStatusEnum.FAILED:
        return <AlertCircle className={`${iconSize} text-red-500`} />;
      default:
        return <Clock className={`${iconSize} text-gray-500`} />;
    }
  };

  if (showLabel) {
    return (
      <span className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium ${statusDisplay.bgColor} ${statusDisplay.textColor} ${className}`}>
        <div className="relative">
          {getIcon()}
          {showPulse && status === DocumentProgressStatusEnum.IN_PROGRESS && (
            <div className="absolute inset-0 animate-ping">
              <div className={`${sizeClasses[size]} rounded-full ${statusDisplay.dotColor} opacity-75`} />
            </div>
          )}
        </div>
        {statusDisplay.label}
      </span>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {getIcon()}
      {showPulse && status === DocumentProgressStatusEnum.IN_PROGRESS && (
        <div className="absolute inset-0 animate-ping">
          <div className={`${sizeClasses[size]} rounded-full ${statusDisplay.dotColor} opacity-75`} />
        </div>
      )}
    </div>
  );
}

interface ProcessingProgressProps {
  total: number;
  completed: number;
  processing: number;
  failed: number;
  showDetails?: boolean;
}

export function ProcessingProgress({ 
  total, 
  completed, 
  processing, 
  failed, 
  showDetails = false 
}: ProcessingProgressProps) {
  const completedPercentage = total > 0 ? (completed / total) * 100 : 0;
  const processingPercentage = total > 0 ? (processing / total) * 100 : 0;
  const failedPercentage = total > 0 ? (failed / total) * 100 : 0;
  
  return (
    <div className="space-y-2">
      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div className="h-full flex">
          <div 
            className="bg-green-500 h-full transition-all duration-500"
            style={{ width: `${completedPercentage}%` }}
          />
          <div 
            className="bg-blue-500 h-full transition-all duration-500 animate-pulse"
            style={{ width: `${processingPercentage}%` }}
          />
          <div 
            className="bg-red-500 h-full transition-all duration-500"
            style={{ width: `${failedPercentage}%` }}
          />
        </div>
      </div>
      
      {/* Details */}
      {showDetails && (
        <div className="flex items-center justify-between text-xs text-gray-600">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span>{completed} completed</span>
            </div>
            {processing > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span>{processing} processing</span>
              </div>
            )}
            {failed > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-red-500 rounded-full" />
                <span>{failed} failed</span>
              </div>
            )}
          </div>
          <span className="font-medium">{total} total</span>
        </div>
      )}
    </div>
  );
}

interface LiveStatusIndicatorProps {
  isPolling: boolean;
  lastUpdated?: string;
  className?: string;
}

export function LiveStatusIndicator({ isPolling, lastUpdated, className = '' }: LiveStatusIndicatorProps) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex items-center gap-1">
        <Radio className={`w-3 h-3 ${isPolling ? 'text-green-500' : 'text-gray-400'}`} />
        <div className={`w-2 h-2 rounded-full ${
          isPolling 
            ? 'bg-green-500 animate-pulse' 
            : 'bg-gray-300'
        }`} />
      </div>
      <span className="text-xs text-gray-500">
        {isPolling ? 'Live updates' : 'Static view'}
      </span>
      {lastUpdated && (
        <span className="text-xs text-gray-400">
          • Updated {new Date(lastUpdated).toLocaleTimeString()}
        </span>
      )}
    </div>
  );
}